<template>
  <div class="results12-wrap">
    <div class="progress"><div class="progress-bar" :style="{ width: progress + '%' }"></div></div>

    <div class="page">

      <!-- RESEARCH NOTE -->
      <div class="research-note">
        <span class="research-note-tag">Research Note · For SEQ Team</span>
        <h1>Design rationale: SEQ Late Night Edition (Gen Z prototype)</h1>
        <p class="research-note-subtitle">A summary of the design choices made in the report below, for review by colleagues unfamiliar with Gen Z conventions.</p>

        <p>This prototype targets <strong>older Gen Z</strong> (roughly age 18 to 26): late-secondary, apprenticeship, university, or early career. Younger Gen Z (school-age) is already covered by the Schoolteacher reports. The design avoids slang that dates quickly and instead relies on <strong>format, tone, and self-aware honesty</strong> — the traits that distinguish content Gen Z trusts from content they dismiss as "adults trying to sound young."</p>

        <p>The 15 considerations below shaped every section of the report. Each one was a deliberate trade-off against more conventional research-report design.</p>

        <div class="research-note-grid">
          <div v-for="(item, i) in researchNotes" :key="item.title" class="research-note-item">
            <div class="research-note-item-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="research-note-item-title">{{ item.title }}</div>
            <div class="research-note-item-body">{{ item.body }}</div>
          </div>
        </div>

        <p><strong>A note on the title.</strong> The report is deliberately NOT labeled "Gen Z Edition." That label would be condescending — the equivalent of "How do you do, fellow kids." Instead it is called "Late Night Edition," which signals the mood (3 AM scrolling, honest, intimate) without naming the demographic. The format does the work; the label stays out of the way.</p>

        <div class="research-note-footer">
          Prototype v0.1 · Mock data based on participant 2841 · BFH Department of Health · May 2026
        </div>
      </div>

      <div class="report-begins">The report begins below</div>

      <!-- THE GEN Z REPORT -->
      <div class="report">

        <!-- SLIDE 01 — INTRO -->
        <section class="slide bg-cream">
          <div class="slide-brand">SEQ · 2026</div>
          <div class="slide-num">01 / 14</div>

          <div class="eyebrow">SWISS EATING QUIZ // LATE NIGHT EDITION</div>
          <h1 class="headline">Your<br>year of <em class="hand-pop-pink">eating</em>,<br>unpacked.</h1>
          <p class="subhead">We took the answers you gave us at 11:47 PM on a Tuesday and turned them into something honest. No diet plan. No fitness influencer. No app to download.</p>

          <div class="sticky tilt-right intro-sticky">
            quick warning: there's<br>data in here ⚠️
          </div>
        </section>

        <!-- SLIDE 02 — THE BIG REVEAL -->
        <section class="slide bg-pink">
          <div class="slide-brand">YOUR YEAR · #2026</div>
          <div class="slide-num">02 / 14</div>

          <div class="eyebrow">YOUR #1 FOOD GROUP</div>
          <div class="big-stat">Dairy.</div>
          <div class="big-stat-context big-stat-context-sm">You consumed roughly <strong>14 servings a week</strong> across yogurt, cheese, and milk. That's more than 73% of Swiss adults. Cheese girl summer was real.</div>

          <div class="honest honest-spaced">
            No judgment. Just data. Cheese is great. We're moving on.
          </div>
        </section>

        <!-- SLIDE 03 — TOP 5 -->
        <section class="slide bg-black">
          <div class="slide-brand">YOUR TOP 5 · 2026</div>
          <div class="slide-num">03 / 14</div>

          <div class="eyebrow">YOUR MOST-EATEN FOOD GROUPS</div>
          <h2 class="headline headline-sm">The greatest hits.</h2>

          <ol class="wrapped-list">
            <li v-for="(item, i) in topFive" :key="item.name">
              <span class="wrapped-rank">{{ String(i + 1).padStart(2, '0') }}</span>
              <span>{{ item.name }}</span>
              <span class="wrapped-amount">{{ item.amount }}</span>
            </li>
          </ol>

          <p class="subhead subhead-spaced">Things you barely touched: legumes, fish, whole grains. They didn't make the cut.</p>
        </section>

        <!-- SLIDE 04 — RECEIPT -->
        <section class="slide bg-mint">
          <div class="slide-brand">RECEIPT · WHAT YOUR FOOD DID</div>
          <div class="slide-num">04 / 14</div>

          <div class="eyebrow">A WEEK OF YOUR EATING, ITEMIZED</div>
          <h2 class="headline headline-md">Here's your<br>nutrition<br>receipt.</h2>

          <div class="receipt">
            <div class="receipt-header">
              <strong>SEQ · WEEK 19</strong>
              <small>09 May 2026 · 14:23</small>
            </div>
            <div v-for="row in receiptRows" :key="row.item" class="receipt-row">
              <span class="item">{{ row.item }}<small>{{ row.detail }}</small></span>
              <span>{{ row.value }}</span>
            </div>
            <div class="receipt-row receipt-total">
              <span>TOTAL</span>
              <span>76/100</span>
            </div>
            <div class="receipt-stamp">★ Thank you for eating. No returns.</div>
          </div>

          <div class="honest">Yes, your body literally keeps a receipt. We just made it readable.</div>
        </section>

        <!-- SLIDE 05 — NOTIFICATIONS -->
        <section class="slide bg-purple">
          <div class="slide-brand">NOTIFICATIONS · TODAY</div>
          <div class="slide-num">05 / 14</div>

          <div class="eyebrow">YOUR BODY SENT YOU SOME ALERTS</div>
          <h2 class="headline headline-xs">Today on<br>"organs in<br>your phone."</h2>

          <div v-for="n in notifications" :key="n.time" class="notification">
            <div class="notification-icon" :class="n.iconColor">{{ n.icon }}</div>
            <div>
              <div class="notification-app">{{ n.app }}</div>
              <div class="notification-body">{{ n.body }}</div>
            </div>
            <div class="notification-time">{{ n.time }}</div>
          </div>

          <div class="honest honest-spaced">
            Your body is talking to you in headaches, mood swings, and energy crashes. We just translated them into push notifications.
          </div>
        </section>

        <!-- SLIDE 06 — GROUP CHAT -->
        <section class="slide bg-white">
          <div class="slide-brand">GROUP CHAT · YOUR ORGANS</div>
          <div class="slide-num">06 / 14</div>

          <div class="eyebrow">A GROUP CHAT INSIDE YOU</div>
          <h2 class="headline headline-sm">"the organs"<br>have been<br>messaging.</h2>

          <div class="group-chat">
            <div class="gc-header">the organs 💀<br><small>6 participants · pinned: stay alive</small></div>

            <div v-for="(msg, i) in groupChat" :key="i" class="gc-msg">
              <div class="gc-avatar" :class="msg.avatarClass">{{ msg.emoji }}</div>
              <div>
                <div class="gc-name">{{ msg.name }}</div>
                <div class="gc-bubble">{{ msg.text }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- SLIDE 07 — POV -->
        <section class="slide bg-blue">
          <div class="slide-brand">POV · COFFEE'S JOURNEY</div>
          <div class="slide-num">07 / 14</div>

          <div class="eyebrow">A QUICK POV</div>
          <h2 class="headline headline-md">POV: you're<br>a coffee bean.</h2>

          <div class="pov">
            <div class="pov-tag">FADE IN</div>
            <p>EXT. <strong>BRAZIL — MORNING</strong>. A farm. Maria picks me from a tree. I'm one of <strong>2,000 beans</strong> she picks today.</p>
            <p>CUT TO: a boat. Months at sea. I cross the equator. Twice.</p>
            <p>EXT. <strong>ZURICH — A WAREHOUSE</strong>. Dark. Cold. A truck arrives.</p>
            <p>INT. <strong>YOUR KITCHEN — 7:42 AM</strong>. A capsule machine hums. <strong>I am pressurized</strong>. I am extracted. I am gone in 12 seconds.</p>
            <p>EXT. <strong>YOUR BRAIN — 7:43 AM</strong>. <strong>Caffeine arrives.</strong> It tells the adenosine receptors to back off. <strong>You feel alive.</strong></p>
            <p>You drink three more of me today.</p>
            <p class="pov-tag pov-tag-bottom">FADE OUT</p>
          </div>

          <div class="honest honest-spaced-sm">
            Maria earned about CHF 11 the day she picked you. The cup at your café costs CHF 4.50. There's a story in that gap.
          </div>
        </section>

        <!-- SLIDE 08 — AESTHETIC -->
        <section class="slide bg-soft-pink">
          <div class="slide-brand">YOUR FOOD AESTHETIC</div>
          <div class="slide-num">08 / 14</div>

          <div class="eyebrow">WE RAN YOUR DATA THROUGH THE VIBE-CHECK</div>
          <h2 class="headline headline-md">Your eating<br>aesthetic is:</h2>

          <div class="profile-card">
            <div class="profile-header">
              <div class="profile-pic">☕</div>
              <div>
                <div class="profile-handle">stressed_swiss_main_character</div>
                <div class="profile-bio">probably typing this from a café</div>
              </div>
            </div>
            <div class="profile-stats-row">
              <div v-for="s in aestheticStats" :key="s.label" class="profile-stat-cell">
                <div class="profile-stat-num">{{ s.value }}</div>
                <div class="profile-stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="profile-body">You exist primarily on dairy, vegetables, and caffeine. Your friends think you cook because of one (1) good pasta you made in 2024. Strong "I will eat one apple and call it lunch" energy.</div>
          </div>

          <div class="aesthetic-grid">
            <div v-for="a in aestheticTiles" :key="a.name" class="aesthetic">
              <div class="aesthetic-emoji">{{ a.emoji }}</div>
              <div class="aesthetic-name">{{ a.name }}</div>
              <div class="aesthetic-match">{{ a.match }}</div>
            </div>
          </div>
        </section>

        <!-- SLIDE 09 — DRAKE MEME -->
        <section class="slide bg-lemon">
          <div class="slide-brand">YOUR PATTERNS · VISUALIZED</div>
          <div class="slide-num">09 / 14</div>

          <div class="eyebrow">SOME PATTERNS WE NOTICED</div>
          <h2 class="headline headline-md">Your eating,<br>in 4 memes.</h2>

          <div class="drake">
            <div class="drake-emoji">🙅‍♀️</div>
            <div class="drake-panel no">Lentils, beans, chickpeas, tofu</div>
          </div>
          <div class="drake">
            <div class="drake-emoji">🙆‍♀️</div>
            <div class="drake-panel yes">Cheese, in all forms, every day</div>
          </div>

          <div class="meme meme-spaced">
            <div class="meme-header">YOUR BREAKFAST DECISIONS</div>
            <div class="meme-row">
              <div class="meme-side no">Cooking eggs</div>
              <div class="meme-side yes">Coffee + maybe yogurt</div>
            </div>
          </div>

          <div class="meme">
            <div class="meme-header">YOUR HYDRATION PRIORITY ORDER</div>
            <div class="meme-row">
              <div class="meme-side yes">Coffee</div>
              <div class="meme-side yes">Water</div>
            </div>
            <p class="meme-footnote">tied for first, sources confirm</p>
          </div>

          <div class="sticky">
            you literally only<br>drank water + coffee.<br>iconic restraint
          </div>
        </section>

        <!-- SLIDE 10 — TWO TRUTHS AND A LIE -->
        <section class="slide bg-orange">
          <div class="slide-brand">QUIZ TIME</div>
          <div class="slide-num">10 / 14</div>

          <div class="eyebrow">A QUICK GAME WITH YOUR DATA</div>
          <h2 class="headline headline-md">Two truths<br>and a lie.<br>About you.</h2>

          <div class="profile-card">
            <div class="ttl-header">YOUR REAL DATA · GUESS THE LIE</div>

            <div class="ttl-option"><strong>A.</strong> You drank approximately <strong>1,460 cups of coffee</strong> this year.</div>
            <div class="ttl-option"><strong>B.</strong> Your diet's carbon footprint equals <strong>driving from Bern to Lisbon</strong> in a small car.</div>
            <div class="ttl-option ttl-option-last"><strong>C.</strong> You eat <strong>more vegetables than the average Swiss adult</strong>.</div>

            <div class="ttl-answer">
              <span class="ttl-answer-label">ANSWER</span><br>
              All three are true. Plot twist. There's no lie. This is just what your data says.
            </div>
          </div>
        </section>

        <!-- SLIDE 11 — HONEST CONFLICT OF INTEREST -->
        <section class="slide bg-cream">
          <div class="slide-brand">FOOTNOTE · IMPORTANT</div>
          <div class="slide-num">11 / 14</div>

          <div class="eyebrow">A NECESSARY HONEST MOMENT</div>
          <h2 class="headline headline-sm">Who's behind<br>this report,<br>actually?</h2>

          <div class="screenshot">
            <div class="screenshot-corner">∙ disclosure ∙</div>
            <div class="screenshot-label">WHO PAID FOR THIS</div>
            <p class="screenshot-p">
              The Swiss Eating Quiz is built at <strong>Bern University of Applied Sciences (BFH)</strong> with public Swiss research funding. There's no app to download. No newsletter. No "personalised meal plan upgrade" for CHF 9.99/month. No supplement code. No fitness coach in the wings.
            </p>
            <p class="screenshot-p screenshot-p-spaced">
              Your anonymous answers help researchers understand how Switzerland actually eats. That's the deal. That's the whole deal.
            </p>
          </div>

          <div class="honest honest-spaced-sm">
            We're saying this because Gen Z asks first who's funding it. Fair question. Answer: you, basically. Tax-funded research. Free at the point of use.
          </div>
        </section>

        <!-- SLIDE 12 — CLIMATE DREAD -->
        <section class="slide bg-green">
          <div class="slide-brand">CLIMATE · YOUR PLATE</div>
          <div class="slide-num">12 / 14</div>

          <div class="eyebrow">THE CLIMATE PART, BUT HONEST</div>
          <h2 class="headline headline-sm">You can't fix<br>climate change<br>alone. We know.</h2>

          <p class="subhead subhead-block">
            Existential dread is built into your generation. We're not going to add to it. We're just going to tell you what your food does.
          </p>

          <div class="profile-card">
            <div class="profile-stats-row profile-stats-row-bare">
              <div class="profile-stat-cell">
                <div class="profile-stat-num text-hot-pink">2.1t</div>
                <div class="profile-stat-label">your CO₂/yr from food</div>
              </div>
              <div class="profile-stat-cell">
                <div class="profile-stat-num text-electric-blue">52%</div>
                <div class="profile-stat-label">imported</div>
              </div>
              <div class="profile-stat-cell">
                <div class="profile-stat-num text-burnt-orange">9</div>
                <div class="profile-stat-label">countries</div>
              </div>
            </div>
          </div>

          <div class="honest honest-spaced-sm">
            Eat one meatless meal a week and you save more carbon than recycling does. That's it. That's the action item. Not "stop eating meat." Not "go vegan." Just: one meal. Move on with your life.
          </div>
        </section>

        <!-- SLIDE 13 — REAL TALK -->
        <section class="slide bg-white">
          <div class="slide-brand">REAL TALK</div>
          <div class="slide-num">13 / 14</div>

          <div class="eyebrow">SOME HONEST OBSERVATIONS</div>
          <h2 class="headline headline-md">Things we<br>actually<br>know.</h2>

          <div class="dm-stack">
            <div class="dm-header">
              <div class="dm-avatar">💭</div>
              <div class="dm-handle">SEQ team<small>online</small></div>
            </div>
            <div class="dm-time">today</div>
            <div class="dm-bubble">cooking from scratch when you're exhausted is genuinely hard. we know this.</div>
            <div class="dm-bubble">groceries are expensive. lentils cost about CHF 2 per pack though. fyi.</div>
            <div class="dm-bubble">you don't owe anyone a "perfect" diet. it doesn't exist.</div>
            <div class="dm-bubble">we're not going to tell you to "meal prep on sunday." nobody actually does that.</div>
            <div class="dm-bubble mine">honestly fair</div>
            <div class="dm-bubble">also: skipping breakfast isn't a moral failing. your brain just runs better with food in it. that's the only argument.</div>
            <div class="dm-bubble">we don't track you. when you close this tab, this report is gone unless you save it.</div>
            <div class="dm-bubble mine">wait you don't have an app??</div>
            <div class="dm-bubble">no. that's the point.</div>
          </div>
        </section>

        <!-- SLIDE 14 — OUTRO -->
        <section class="slide bg-black">
          <div class="slide-brand">END · SHARE IF YOU WANT</div>
          <div class="slide-num">14 / 14</div>

          <div class="eyebrow">YOUR YEAR · WRAPPED</div>
          <h2 class="headline-xl">That's<br>your<br>year.</h2>

          <div class="share-square">
            <div class="share-square-tag">SEQ · 2026</div>
            <div class="share-square-headline">
              I ate 1,460 cups of coffee, 0 lentils, and somehow stayed alive.<br>
              <span class="share-attrib">— my year of eating</span>
            </div>
            <div class="share-square-foot">swisseatingquiz.ch · take it yourself</div>
          </div>

          <div class="outro-share">
            <button type="button">SHARE</button>
            <button type="button">SAVE</button>
            <button type="button">RETAKE IN 3MO</button>
          </div>

          <div class="honest honest-spaced-xs">
            take the SEQ again in 3 months if you want to see what changed. or don't. nothing happens either way. genuinely. that's the deal.
          </div>

          <div class="end-meta">
            SEQ · BFH · Bern · 2026<br>
            Late Night Edition · v0.1
          </div>
        </section>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Results12',
  data() {
    return {
      progress: 0,
      onScroll: null,
      researchNotes: [
        { title: 'No fake slang', body: 'Nothing dates faster than "rizz" written by a 50-year-old. Tone over vocabulary.' },
        { title: 'Aesthetic over polish', body: 'Slight imperfection (sticky notes, hand-drawn text) signals authenticity, not laziness.' },
        { title: 'Self-aware humor', body: 'Content acknowledges its own format. "This is the part where adults moralize. We won\'t."' },
        { title: 'Spotify Wrapped DNA', body: 'Big numbers, bold colors, surprising facts, shareable slides. The format Gen Z genuinely loves.' },
        { title: 'Mobile-first vertical scroll', body: 'Each slide sized roughly to a phone screen. No horizontal layouts. Built for thumb scrolling.' },
        { title: 'Screenshot aesthetic', body: 'Fake notifications, fake DMs, fake group chats, fake receipts. Familiar visual vocabulary.' },
        { title: 'Memes used carefully', body: 'Visual memes (Drake two-panel) age better than verbal slang. Specific cultural references avoided.' },
        { title: 'Mental health acknowledged', body: '"Cooking when exhausted is hard" rather than "meal-prep Sunday is fun." Recognition of difficulty.' },
        { title: 'Institutionally skeptical', body: 'Names funding sources and conflicts of interest. Doesn\'t lean on "experts say."' },
        { title: 'Climate dread is baseline', body: 'Sustainability is folded in honestly. "You alone can\'t fix it, but here\'s how this fits."' },
        { title: 'Cost transparency', body: 'Real CHF prices. "Lentils cost CHF 2." Treats financial reality as relevant.' },
        { title: 'Time honesty', body: '"This takes 7 minutes" beats "great recipe!" Respects the user\'s actual schedule.' },
        { title: 'No moralizing about food', body: 'No "good" or "bad" foods. No weight talk. No restriction language. No body image triggers.' },
        { title: 'Diet diversity respected', body: 'Vegan, omnivore, religious, recovery from eating disorder: all treated as legitimate starting points.' },
        { title: 'Transparent about institutional purpose', body: 'Names BFH as the source. "This data helps our research." No hidden agenda framing.' },
      ],
      topFive: [
        { name: 'Dairy', amount: '14×/wk' },
        { name: 'Vegetables', amount: '12×/wk' },
        { name: 'Grains', amount: '11×/wk' },
        { name: 'Fruit', amount: '9×/wk' },
        { name: 'Animal protein', amount: '7×/wk' },
      ],
      receiptRows: [
        { item: 'Vegetables', detail: '27 portions', value: '+88 nutrition' },
        { item: 'Coffee × 28', detail: '4/day, every day', value: '+focus, -sleep' },
        { item: 'Dairy', detail: '14 servings', value: '+calcium' },
        { item: 'Whole grains', detail: 'only 50g', value: '−fiber' },
        { item: 'Legumes', detail: '0 portions', value: '−protein, −iron' },
        { item: 'Fish', detail: '0 portions', value: '−omega-3' },
      ],
      notifications: [
        { iconColor: 'brain', icon: '🧠', app: 'your brain', body: 'low on glucose. you skipped breakfast again. fix?', time: '9:47' },
        { iconColor: 'gut', icon: '🌱', app: 'your gut bacteria', body: "we've been ASKING for fiber. 100 trillion of us. polite request.", time: '11:02' },
        { iconColor: 'body', icon: '❤️', app: 'your iron stores', body: 'that 4th coffee blocked iron absorption. just so you know.', time: '15:31' },
        { iconColor: 'sleep', icon: '😴', app: 'your growth hormone', body: "it's 1am. we need you to sleep so we can do our job.", time: '1:14' },
      ],
      groupChat: [
        { avatarClass: 'brain', emoji: '🧠', name: 'brain', text: "guys. who's drinking the 4th coffee. i'm vibrating" },
        { avatarClass: 'muscle', emoji: '💪', name: 'muscles', text: "also where's the protein at lunch. half a sandwich. unserious" },
        { avatarClass: 'bones', emoji: '🦴', name: 'bones', text: "i'm fed. dairy queen. no complaints from me" },
        { avatarClass: 'gut', emoji: '🌱', name: 'gut', text: "we've gotta talk about the fiber situation. i'm BEGGING for lentils" },
        { avatarClass: 'heart', emoji: '❤️', name: 'heart', text: "processed meat 3x this week. love that for me. (i'm being sarcastic)" },
        { avatarClass: 'skin', emoji: '✨', name: 'skin', text: 'add some berries please. vitamin c. doing my best out here' },
        { avatarClass: 'brain', emoji: '🧠', name: 'brain', text: "also. sleep. that's the only ask. just go to sleep before midnight" },
      ],
      aestheticStats: [
        { value: '76%', label: 'Swiss canteen-core' },
        { value: '14%', label: 'aspiring foodie' },
        { value: '10%', label: '3am snack goblin' },
      ],
      aestheticTiles: [
        { emoji: '☕', name: 'Café-girl-aesthetic', match: '92% match' },
        { emoji: '🥗', name: 'Salad-bar-warrior', match: '71% match' },
        { emoji: '🍞', name: 'Bread-with-everything', match: '68% match' },
        { emoji: '🌱', name: 'Hypothetical-vegetarian', match: '34% match' },
      ],
    };
  },
  mounted() {
    this.onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop / (h.scrollHeight - h.clientHeight) * 100;
      this.progress = Math.max(0, Math.min(100, scrolled || 0));
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeUnmount() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Space+Grotesk:wght@500;600;700&family=Caveat:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

.results12-wrap {
  --ink: #0F0F0F;
  --ink-soft: #2D2D2D;
  --ink-mute: #707070;
  --paper: #FAFAFA;
  --hot-pink: #FF2D87;
  --acid-green: #B8FF3D;
  --electric-blue: #2E5FFF;
  --soft-purple: #9D7FFF;
  --burnt-orange: #FF6E2C;
  --cream: #FFF1D6;
  --mint: #82F4C8;
  --lemon: #FFEC4D;
  --soft-pink: #FFC9DE;
  --mono: 'JetBrains Mono', monospace;
  --display: 'Space Grotesk', sans-serif;
  --body: 'Inter', sans-serif;
  --hand: 'Caveat', cursive;

  font-family: var(--body);
  font-size: 16px;
  line-height: 1.55;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results12-wrap *,
.results12-wrap *::before,
.results12-wrap *::after {
  box-sizing: border-box;
}

.page { max-width: 1100px; margin: 0 auto; }

/* RESEARCH NOTE */
.research-note {
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  border-left: 4px solid var(--ink);
  margin: 32px;
  padding: 28px 32px;
  font-family: var(--body);
  font-size: 14px;
  line-height: 1.65;
  color: var(--ink-soft);
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.research-note-tag {
  display: inline-block;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-mute);
  background: #F5F5F5;
  padding: 4px 10px;
  border-radius: 3px;
  margin-bottom: 14px;
}
.research-note h1 {
  font-family: var(--display);
  font-size: 22px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 6px;
  letter-spacing: -0.01em;
}
.research-note-subtitle {
  font-size: 13px;
  color: var(--ink-mute);
  margin-bottom: 20px;
  font-style: italic;
}
.research-note p {
  margin-bottom: 14px;
}
.research-note strong {
  color: var(--ink);
  font-weight: 600;
}
.research-note-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 20px;
  margin: 18px 0;
  padding: 16px 0;
  border-top: 1px dashed #D4D4D4;
  border-bottom: 1px dashed #D4D4D4;
}
.research-note-item { font-size: 13px; }
.research-note-item-num {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
  margin-bottom: 2px;
  font-weight: 500;
}
.research-note-item-title {
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 2px;
}
.research-note-item-body {
  color: var(--ink-mute);
  font-size: 12px;
  line-height: 1.5;
}
.research-note-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E5E5E5;
  font-size: 12px;
  color: var(--ink-mute);
  font-style: italic;
}

.report-begins {
  text-align: center;
  margin: 48px 32px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.2em;
  color: var(--ink-mute);
  text-transform: uppercase;
  position: relative;
}
.report-begins::before,
.report-begins::after {
  content: '';
  display: inline-block;
  width: 80px;
  height: 1px;
  background: var(--ink-mute);
  vertical-align: middle;
  margin: 0 12px;
}

/* MOBILE-STYLE REPORT */
.report {
  max-width: 480px;
  margin: 0 auto;
  background: var(--paper);
}

.slide {
  padding: 56px 28px;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.slide-num {
  position: absolute;
  top: 24px;
  right: 28px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  opacity: 0.6;
  font-weight: 500;
}
.slide-brand {
  position: absolute;
  top: 24px;
  left: 28px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  opacity: 0.6;
  font-weight: 500;
}

.slide.bg-pink { background: var(--hot-pink); color: white; }
.slide.bg-green { background: var(--acid-green); color: var(--ink); }
.slide.bg-blue { background: var(--electric-blue); color: white; }
.slide.bg-purple { background: var(--soft-purple); color: white; }
.slide.bg-orange { background: var(--burnt-orange); color: white; }
.slide.bg-cream { background: var(--cream); color: var(--ink); }
.slide.bg-mint { background: var(--mint); color: var(--ink); }
.slide.bg-lemon { background: var(--lemon); color: var(--ink); }
.slide.bg-soft-pink { background: var(--soft-pink); color: var(--ink); }
.slide.bg-black { background: var(--ink); color: white; }
.slide.bg-white { background: white; color: var(--ink); }

.eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 14px;
}
.headline {
  font-family: var(--display);
  font-size: 56px;
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
}
.headline-xs { font-size: 36px; }
.headline-sm { font-size: 38px; }
.headline-md { font-size: 40px; }
.headline-xl {
  font-family: var(--display);
  font-size: 88px;
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.04em;
  margin-bottom: 16px;
}
.subhead {
  font-size: 19px;
  line-height: 1.4;
  font-weight: 400;
  opacity: 0.9;
  max-width: 90%;
}
.subhead-spaced { margin-top: 28px; }
.subhead-block { margin: 24px 0; }

.hand-pop-pink {
  font-family: var(--hand);
  font-style: normal;
  color: var(--hot-pink);
  font-size: 70px;
  line-height: 0.9;
}

/* BIG STAT */
.big-stat {
  font-family: var(--display);
  font-size: 200px;
  font-weight: 800;
  line-height: 0.85;
  letter-spacing: -0.05em;
  margin: 12px 0;
}
.big-stat-context {
  font-size: 16px;
  margin-top: 28px;
  max-width: 85%;
  opacity: 0.9;
  line-height: 1.5;
}
.big-stat-context-sm { font-size: 18px; }

/* NOTIFICATION */
.notification {
  background: rgba(255,255,255,0.95);
  color: var(--ink);
  border-radius: 18px;
  padding: 14px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.4;
  margin: 12px 0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 10px;
  align-items: start;
}
.notification-icon {
  width: 32px;
  height: 32px;
  background: var(--ink);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.notification-icon.brain { background: var(--electric-blue); }
.notification-icon.gut { background: var(--acid-green); color: var(--ink); }
.notification-icon.body { background: var(--hot-pink); }
.notification-icon.sleep { background: var(--soft-purple); }
.notification-app {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-mute);
  margin-bottom: 2px;
}
.notification-body { font-weight: 500; }
.notification-time {
  font-size: 11px;
  color: var(--ink-mute);
  font-weight: 500;
}

/* DM */
.dm-stack {
  background: rgba(255,255,255,0.95);
  color: var(--ink);
  border-radius: 24px;
  padding: 20px;
  margin: 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  box-shadow: 0 6px 24px rgba(0,0,0,0.15);
}
.dm-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid #EEE;
  margin-bottom: 14px;
}
.dm-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--electric-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.dm-handle {
  font-weight: 600;
  font-size: 14px;
}
.dm-handle small {
  display: block;
  font-weight: 400;
  font-size: 11px;
  color: var(--ink-mute);
}
.dm-bubble {
  background: #F1F1F3;
  border-radius: 18px;
  padding: 10px 14px;
  margin-bottom: 6px;
  font-size: 14px;
  max-width: 85%;
  line-height: 1.4;
}
.dm-bubble.mine {
  background: var(--electric-blue);
  color: white;
  margin-left: auto;
}
.dm-time {
  font-size: 10px;
  color: var(--ink-mute);
  text-align: center;
  margin: 12px 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* WRAPPED LIST */
.wrapped-list {
  list-style: none;
  font-family: var(--display);
  padding: 0;
}
.wrapped-list li {
  display: grid;
  grid-template-columns: 60px 1fr auto;
  gap: 14px;
  align-items: baseline;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  font-size: 24px;
  font-weight: 600;
}
.bg-blue .wrapped-list li,
.bg-pink .wrapped-list li,
.bg-purple .wrapped-list li,
.bg-orange .wrapped-list li,
.bg-black .wrapped-list li { border-bottom-color: rgba(255,255,255,0.18); }
.wrapped-rank {
  font-size: 40px;
  font-weight: 800;
  opacity: 0.5;
}
.wrapped-amount {
  font-family: var(--mono);
  font-size: 15px;
  font-weight: 500;
  opacity: 0.7;
}

/* SCREENSHOT */
.screenshot {
  background: white;
  color: var(--ink);
  border-radius: 14px;
  padding: 18px;
  margin: 14px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  box-shadow: 0 6px 24px rgba(0,0,0,0.18);
  position: relative;
}
.screenshot-corner {
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.screenshot-label {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}
.screenshot-p {
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}
.screenshot-p-spaced { margin-top: 12px; }

/* STICKY */
.sticky {
  background: var(--lemon);
  color: var(--ink);
  padding: 16px 20px;
  font-family: var(--hand);
  font-size: 22px;
  font-weight: 600;
  border-radius: 4px;
  box-shadow: 4px 4px 0 rgba(0,0,0,0.15);
  margin: 14px 0;
  transform: rotate(-2deg);
  max-width: 280px;
}
.sticky.tilt-right { transform: rotate(1.5deg); }
.intro-sticky { margin-top: 32px; }

/* HONEST */
.honest {
  font-family: var(--mono);
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.75;
  padding: 14px 16px;
  border-left: 2px solid currentColor;
  margin: 18px 0;
}
.honest-spaced { margin-top: 32px; }
.honest-spaced-sm { margin-top: 24px; }
.honest-spaced-xs { margin-top: 28px; }

/* RECEIPT */
.receipt {
  background: white;
  color: var(--ink);
  font-family: var(--mono);
  padding: 24px 20px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.7;
  max-width: 340px;
  margin: 16px 0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}
.receipt-header {
  text-align: center;
  border-bottom: 1px dashed #999;
  padding-bottom: 10px;
  margin-bottom: 10px;
}
.receipt-header strong {
  font-size: 14px;
  letter-spacing: 0.04em;
  display: block;
  margin-bottom: 4px;
}
.receipt-header small {
  font-size: 10px;
  color: var(--ink-mute);
}
.receipt-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  padding: 4px 0;
}
.receipt-row .item small {
  display: block;
  color: var(--ink-mute);
  font-size: 10px;
}
.receipt-total {
  border-top: 1px dashed #999;
  margin-top: 10px;
  padding-top: 10px;
  font-weight: 700;
}
.receipt-stamp {
  margin-top: 14px;
  text-align: center;
  font-size: 11px;
  color: var(--ink-mute);
  font-style: italic;
}

/* MEME */
.meme {
  background: white;
  color: var(--ink);
  border-radius: 14px;
  padding: 18px;
  margin: 14px 0;
  font-family: var(--body);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}
.meme-spaced { margin-top: 24px; }
.meme-header {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--ink-mute);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.meme-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.meme-side {
  padding: 14px;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}
.meme-side.no {
  background: #FFE0E0;
  text-decoration: line-through;
  text-decoration-color: var(--hot-pink);
  opacity: 0.7;
}
.meme-side.yes {
  background: var(--acid-green);
  color: var(--ink);
}
.meme-footnote {
  text-align: center;
  margin-top: 10px;
  font-size: 12px;
  color: var(--ink-mute);
  font-family: var(--mono);
}

/* PROFILE CARD */
.profile-card {
  background: white;
  color: var(--ink);
  border-radius: 24px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 6px 30px rgba(0,0,0,0.18);
  font-family: var(--body);
}
.profile-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}
.profile-pic {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--hot-pink), var(--soft-purple));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}
.profile-handle {
  font-weight: 700;
  font-size: 15px;
}
.profile-bio {
  font-size: 13px;
  color: var(--ink-mute);
}
.profile-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 14px 0;
  border-top: 1px solid #EEE;
  border-bottom: 1px solid #EEE;
  margin-bottom: 14px;
}
.profile-stats-row-bare {
  border: none;
  padding: 0;
  margin: 0;
}
.profile-stat-cell {
  text-align: center;
}
.profile-stat-num {
  font-family: var(--display);
  font-weight: 700;
  font-size: 22px;
}
.profile-stat-label {
  font-size: 10px;
  color: var(--ink-mute);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}
.profile-body { font-size: 14px; line-height: 1.55; }

.text-hot-pink { color: var(--hot-pink); }
.text-electric-blue { color: var(--electric-blue); }
.text-burnt-orange { color: var(--burnt-orange); }

/* AESTHETIC */
.aesthetic-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 18px 0;
}
.aesthetic {
  background: rgba(255,255,255,0.95);
  color: var(--ink);
  border-radius: 14px;
  padding: 16px;
  font-family: var(--display);
}
.aesthetic-emoji { font-size: 36px; line-height: 1; margin-bottom: 8px; }
.aesthetic-name {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
}
.aesthetic-match {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* GROUP CHAT */
.group-chat {
  background: white;
  color: var(--ink);
  border-radius: 20px;
  padding: 18px;
  margin: 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
}
.gc-header {
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #EEE;
  margin-bottom: 14px;
}
.gc-header small {
  display: block;
  font-weight: 400;
  font-size: 11px;
  color: var(--ink-mute);
  margin-top: 2px;
}
.gc-msg {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
}
.gc-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: var(--electric-blue);
}
.gc-avatar.brain { background: var(--electric-blue); }
.gc-avatar.heart { background: var(--hot-pink); }
.gc-avatar.gut { background: var(--acid-green); color: var(--ink); }
.gc-avatar.muscle { background: var(--burnt-orange); }
.gc-avatar.bones { background: var(--soft-purple); }
.gc-avatar.skin { background: var(--soft-pink); color: var(--ink); }
.gc-name {
  font-weight: 600;
  font-size: 11px;
  margin-bottom: 2px;
  color: var(--ink-mute);
}
.gc-bubble {
  background: #F1F1F3;
  border-radius: 14px;
  padding: 8px 12px;
  display: inline-block;
  line-height: 1.45;
}

/* POV */
.pov {
  background: var(--ink);
  color: white;
  padding: 28px 24px;
  border-radius: 14px;
  margin: 18px 0;
  font-family: var(--mono);
  font-size: 13px;
  line-height: 1.7;
}
.pov p { margin: 0 0 12px 0; }
.pov p:last-child { margin-bottom: 0; }
.pov-tag {
  font-size: 10px;
  letter-spacing: 0.2em;
  color: var(--acid-green);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.pov-tag-bottom { margin-top: 18px; }
.pov strong { color: var(--lemon); }

/* TTL (Two truths) */
.ttl-header {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--ink-mute);
  margin-bottom: 14px;
}
.ttl-option {
  padding: 14px;
  border-radius: 10px;
  background: #F8F8F8;
  margin-bottom: 10px;
}
.ttl-option-last { margin-bottom: 0; }
.ttl-answer {
  margin-top: 18px;
  padding: 14px;
  background: var(--ink);
  color: white;
  border-radius: 10px;
  font-size: 13px;
}
.ttl-answer-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  opacity: 0.7;
}

/* SHARE SQUARE */
.share-square {
  aspect-ratio: 1 / 1;
  background: var(--ink);
  color: white;
  padding: 32px;
  border-radius: 24px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}
.share-square::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, var(--hot-pink), var(--soft-purple), var(--electric-blue));
  opacity: 0.95;
  z-index: 0;
}
.share-square > * { position: relative; z-index: 1; }
.share-square-tag {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.85;
}
.share-square-headline {
  font-family: var(--display);
  font-size: 36px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
}
.share-attrib {
  font-family: var(--hand);
  font-size: 28px;
  font-weight: 600;
}
.share-square-foot {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  opacity: 0.7;
}

/* DRAKE */
.drake {
  background: white;
  color: var(--ink);
  border-radius: 16px;
  padding: 14px;
  margin: 14px 0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 12px;
  align-items: center;
}
.drake-panel {
  padding: 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}
.drake-panel.no { background: #FFE0E0; }
.drake-panel.yes { background: var(--acid-green); }
.drake-emoji {
  text-align: center;
  font-size: 48px;
}

/* OUTRO */
.outro-share {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 24px;
}
.outro-share button {
  background: rgba(255,255,255,0.15);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 10px 8px;
  border-radius: 100px;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  font-weight: 500;
}
.end-meta {
  margin-top: 36px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  opacity: 0.5;
  text-transform: uppercase;
}

/* PROGRESS BAR */
.progress {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: rgba(0,0,0,0.05);
  z-index: 100;
}
.progress-bar {
  height: 100%;
  background: var(--ink);
  width: 0;
  transition: width 0.1s;
}
</style>
